import htmx from "htmx.org";

window.htmx = htmx;

// Single reusable Intl formatter for local "YYYY-MM-DD HH:MM" (24h) rendering.
const LOCAL_DTF = new Intl.DateTimeFormat("en-CA", {
  year: "numeric", month: "2-digit", day: "2-digit",
  hour: "2-digit", minute: "2-digit", hourCycle: "h23",
});

window.rwatcher = {
  openDialog: (id) => document.getElementById(id)?.showModal(),
  closeDialog: (id) => document.getElementById(id)?.close(),

  /** Whether an HTMX request ended on the login page (the session had expired). */
  landedOnLogin(xhr) {
    try {
      return new URL(xhr.responseURL).pathname === "/auth/login";
    } catch {
      return false;
    }
  },

  // Reusable date/time helpers. The app stores instants in UTC; these render
  // them for humans and can be called from any page or inline script via
  // `window.rwatcher.datetime`.
  datetime: {
    /** The viewer's IANA timezone, e.g. "Europe/Paris" (or "" if unknown). */
    localZone() {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    },

    /**
     * Format an ISO-8601 UTC instant in the viewer's local timezone as
     * "YYYY-MM-DD HH:MM" (24h). Returns "" for an unparseable input.
     */
    toLocal(iso) {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return "";
      const p = LOCAL_DTF.formatToParts(d).reduce((acc, x) => ((acc[x.type] = x.value), acc), {});
      return `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}`;
    },

    /**
     * Format an ISO-8601 UTC instant for a tooltip as
     * "YYYY-MM-DD HH:MM:SS (UTC)": a space instead of the ISO "T", seconds
     * precision (no milliseconds), and no trailing "Z" (the "(UTC)" says it).
     * Returns "" for an empty input.
     */
    utcTooltip(iso) {
      if (!iso) return "";
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return "";
      const clean = String(iso).replace(/\.\d+/, "").replace("T", " ").replace(/Z$/, "");
      return `${clean} (UTC)`;
    },
  },
};

(() => {
  const isMac = /Mac|iPhone|iPad|iPod/i.test(navigator.platform || navigator.userAgent);

  document.addEventListener("keydown", (event) => {
    if (event.key !== "k" && event.key !== "K") return;
    const triggerKey = isMac ? event.metaKey : event.ctrlKey;
    if (!triggerKey || event.altKey || event.shiftKey) return;
    const target = document.querySelector("[data-shortcut='search']");
    if (!target) return;
    event.preventDefault();
    // Below the nav breakpoint the navbar search sits in the collapsed menu,
    // where it cannot take focus: open the menu first.
    const menuToggle = document.getElementById("nav-toggle");
    if (menuToggle && target.getClientRects().length === 0 && target.closest("#nav-menu")) {
      menuToggle.checked = true;
    }
    target.focus();
    if (typeof target.select === "function") target.select();
  });

  const reveal = () => {
    document.querySelectorAll("[data-shortcut-hint]").forEach((el) => {
      el.textContent = isMac ? "⌘K" : "Ctrl+K";
      el.classList.remove("hidden");
    });
  };
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", reveal);
  } else {
    reveal();
  }
})();

// Navbar global search: terse placeholder at rest (the box is compact and would
// otherwise truncate the text mid-word), fuller one once focused — the box
// widens on focus, so there's room. Purely cosmetic; no-JS viewers keep the
// rest placeholder, which fits without truncation.
(() => {
  const input = document.querySelector(".nav-search input[data-placeholder-focus]");
  if (!input) return;
  const rest = input.getAttribute("placeholder") || "";
  const focused = input.getAttribute("data-placeholder-focus") || rest;
  input.addEventListener("focus", () => { input.placeholder = focused; });
  input.addEventListener("blur", () => { input.placeholder = rest; });
})();

// Collapsed navbar menu (narrow viewports). The menu is a CSS checkbox toggle
// that works without JS; this adds the keyboard behaviour of a button and keeps
// the menu closed when a page is shown again from the back/forward cache.
(() => {
  const toggle = document.getElementById("nav-toggle");
  const menu = document.getElementById("nav-menu");
  if (!toggle || !menu) return;

  // A checkbox toggles on Space only; the control looks like a button, so Enter
  // toggles it too.
  toggle.addEventListener("keydown", (event) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    toggle.click();
  });

  // Escape closes the menu, unless another component owns the key: an open
  // <dialog> (it closes itself), an IME composition, or a search field that
  // still holds text (Escape clears it first).
  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape" || !toggle.checked) return;
    if (event.isComposing || event.defaultPrevented) return;
    if (document.querySelector("dialog[open]")) return;
    const active = document.activeElement;
    if (active && active.type === "search" && active.value) return;
    const focusWasInMenu = menu.contains(active);
    toggle.checked = false;
    if (focusWasInMenu) toggle.focus();
  });

  window.addEventListener("pageshow", (event) => {
    if (event.persisted) toggle.checked = false;
  });
})();

// Localise server-rendered UTC timestamps to the viewer's timezone. The server
// emits a canonical instant in <time datetime="...Z"> with a readable UTC
// fallback as text; here we rewrite that text to local time and label the
// column header with the resolved IANA zone. No-JS viewers keep the UTC value.
(() => {
  const dt = window.rwatcher.datetime;
  const localize = (root) => {
    root.querySelectorAll("time[data-localize-dt]").forEach((el) => {
      const iso = el.getAttribute("datetime");
      if (!iso) return;
      const local = dt.toLocal(iso);
      if (!local) return;
      el.textContent = local;
      el.title = dt.utcTooltip(iso);
    });
    const tz = dt.localZone();
    if (tz) {
      root.querySelectorAll("[data-tz-label]").forEach((el) => {
        el.textContent = `(${tz})`;
      });
    }
  };
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => localize(document));
  } else {
    localize(document);
  }
  // Content swapped in by HTMX (the component page header) arrives with UTC text again.
  document.body.addEventListener("htmx:load", (event) => {
    if (event.detail && event.detail.elt) localize(event.detail.elt);
  });
})();

// A request sent after the session expired follows Form Auth's redirect and receives
// the login page with a 200: swapped in, it would show a fragment of the login form,
// and a request that swaps nothing would look successful. Go to the login page instead.
(() => {
  document.body.addEventListener("htmx:beforeSwap", (event) => {
    const xhr = event.detail && event.detail.xhr;
    if (!xhr || !window.rwatcher.landedOnLogin(xhr)) return;
    event.detail.shouldSwap = false;
    window.location.assign(xhr.responseURL);
  });
})();

// Refresh button: the crawl now runs asynchronously, so the POST returns
// instantly with no visible change. Acknowledge the click with a transient
// "queued" state (spinning icon, blocked re-clicks); it reverts after a few
// seconds — or on the next page reload, when fresh data lands. The server keys
// the crawl job per component (replace=true), so repeated clicks never pile up.
(() => {
  const QUEUED_MS = 5000;
  document.body.addEventListener("htmx:afterRequest", (event) => {
    const form = event.detail && event.detail.elt;
    if (!form || !form.matches || !form.matches("[data-refresh-form]")) return;
    if (!event.detail.successful || window.rwatcher.landedOnLogin(event.detail.xhr)) return;
    const btn = form.querySelector("[data-refresh-btn]");
    if (!btn) return;
    btn.classList.add("is-queued");
    btn.disabled = true;
    btn.setAttribute("aria-busy", "true");
    btn.title = "Crawl queued — running in the background";
    window.setTimeout(() => {
      btn.classList.remove("is-queued");
      btn.disabled = false;
      btn.removeAttribute("aria-busy");
      btn.removeAttribute("title");
    }, QUEUED_MS);
  });
})();

// Copy-link buttons (`[data-copy-url]`): copy the URL to the clipboard and confirm
// through the button's tooltip. The async Clipboard API only exists in a secure
// context (HTTPS or localhost) and can still be refused by a permissions policy,
// so plain-HTTP hosts such as the local dev vhost, and refusals, fall back to a
// hidden textarea + execCommand("copy"). Buttons ship `hidden` and are revealed
// here, since they are useless without JS.
(() => {
  const FEEDBACK_MS = 1500;

  const copyWithTextarea = (text) => {
    const previousFocus = document.activeElement;
    const area = document.createElement("textarea");
    area.value = text;
    area.setAttribute("readonly", "");
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    try {
      if (!document.execCommand("copy")) throw new Error("copy command rejected");
    } finally {
      area.remove();
      if (previousFocus && typeof previousFocus.focus === "function") previousFocus.focus();
    }
  };

  const copyText = async (text) => {
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        return;
      } catch {
        // Refused (permissions policy, embedded frame): try the legacy path.
      }
    }
    copyWithTextarea(text);
  };

  const reveal = () => {
    document.querySelectorAll("[data-copy-url]").forEach((btn) => { btn.hidden = false; });
  };
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", reveal);
  } else {
    reveal();
  }

  const timers = new WeakMap();
  document.addEventListener("click", async (event) => {
    const btn = event.target.closest && event.target.closest("[data-copy-url]");
    if (!btn) return;
    if (!btn.dataset.restTip) btn.dataset.restTip = btn.dataset.tip || "";
    let copied = true;
    try {
      await copyText(btn.dataset.copyUrl);
    } catch {
      copied = false;
    }
    const message = copied ? "Link copied" : "Copy failed";
    btn.dataset.tip = message;
    btn.classList.toggle("is-copied", copied);
    btn.classList.add("tooltip-open");
    const status = btn.parentElement && btn.parentElement.querySelector("[data-copy-status]");
    if (status) status.textContent = message;
    window.clearTimeout(timers.get(btn));
    timers.set(btn, window.setTimeout(() => {
      btn.dataset.tip = btn.dataset.restTip;
      btn.classList.remove("is-copied", "tooltip-open");
      if (status) status.textContent = "";
    }, FEEDBACK_MS));
  });
})();
